import {
  genebankDeletePreview,
  genebankEmptyPreview,
  genebankIntakePreview,
  genebankMovePreview,
  genebankWithdrawPreview,
  getGenebankExportUrl,
  useGenebankAccessions,
  useGenebankContainers,
  useGenebankDeleteRun,
  useGenebankEmptyRun,
  useGenebankIntakeRun,
  useGenebankMoveRun,
  useGenebankWithdrawRun,
} from "@api/generated/genebank/genebank";
import type { Accession, Container, IntakeDraft, IntakeIn, Move, Removal, WithdrawIn, WithdrawRow } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { usePreview } from "@app/preview";
import { SeasonMissing } from "@app/SeasonMissing";
import { useTrial } from "@app/trials";
import { hasRole, useAutosave, useCreateSeason, useSeason, useSeasonFromLink, useSettings } from "@app/workspace";
import { LineLink } from "@features/genealogy/GenotypeParts";
import { keyText } from "@features/genealogy/genotype";
import { hint } from "@i18n";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/genebank/genebank.css";
import { AmountCell } from "@features/genebank/AmountCell";
import { candidateGroups, pickKey, picksFor } from "@features/genebank/intake";
import { kgColumn, useGenebankChanged } from "@features/genebank/parts";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { parseNumberText } from "@features/trials/clipboard";
import {
  Autocomplete,
  Button,
  Checkbox,
  Loader,
  MultiSelect,
  NumberInput,
  Popover,
  Radio,
  SegmentedControl,
  Select,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ActionMenu } from "@ui/ActionMenu";
import { cellText, formatNumber, fullTime, num } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { RemovableChip } from "@ui/RemovableChip";
import { SearchBox } from "@ui/SearchBox";
import { SelectionBar } from "@ui/SelectionBar";
import { Splitter } from "@ui/Splitter";
import { kindColumn } from "@ui/table/columns";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { usePicks } from "@ui/table/usePicks";
import { YearInput } from "@ui/YearInput";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

// the legacy filters: what each takes (text is upper-cased, numbers are whole unless kg)
const FILTERS = {
  pgm: { kind: "text", min: 0 },
  gh: { kind: "text", min: 0 },
  max_bags: { kind: "int", min: 1 }, // a line with some bags, at most this many
  older_than: { kind: "int", min: 0 },
  below_kg: { kind: "kg", min: 0 },
} as const;
type FilterKey = keyof typeof FILTERS;

const searchSchema = z.object({
  container: z.string().optional(),
  q: z.string().optional(),
  pgm: z.string().optional(),
  gh: z.string().optional(),
  max_bags: z.number().int().min(1).optional(),
  older_than: z.number().int().optional(),
  below_kg: z.number().min(0).optional(),
  // the lines of these trials of that season (the selection's "GeneBank Also" link)
  lines_ev: z.number().int().optional(),
  lines_of: z.array(z.string()).optional(),
  order: z.enum(["slot", "name"]).optional(),
  // only the items never counted (bags) / never weighed (kg): the legacy 888 / 999 codes
  uncounted: z.boolean().optional(),
  unweighed: z.boolean().optional(),
  // a withdrawal's target, when a link names it (the selection's target trial)
  to_ev: z.number().int().optional(),
  to_kis: z.string().optional(),
  panel: z.enum(["intake", "withdraw"]).optional(),
});
type Search = z.infer<typeof searchSchema>;
type Order = NonNullable<Search["order"]>;

export const Route = createFileRoute("/genebank/genebank")({ validateSearch: searchSchema, component: GenebankPage });

/** Génbank és magposta → Génbank (the legacy GeneBank menu): accessions by container, intake and refresh from trial
 *  plots, withdrawal into a trial, empty accessions deleted. */
function GenebankPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canEdit = hasRole(useUserMe().data?.roles, "genbank");
  const season = useSeasonFromLink(undefined);
  const set = useCallback((patch: Partial<Search>) => void navigate({ search: (s) => ({ ...s, ...patch }) }), [navigate]);
  const setQuery = useCallback((v: string) => void navigate({ search: (s) => ({ ...s, q: v || undefined }), replace: true }), [navigate]);
  const { panel, q, to_ev, to_kis, ...filter } = search;
  const params = { ...filter, q: q ?? "" };
  // the server's default: by name for the gene-bank-wide bag report (the legacy "Bag No Less Than"), else by slot
  const order: Order = search.order ?? (search.max_bags != null && !search.container ? "name" : "slot");
  const containers = useGenebankContainers({ query: { staleTime: 60_000 } }).data ?? [];
  const list = useGenebankAccessions(params, { query: { placeholderData: keepPreviousData } });
  const [picked, setPicked] = useState<number[]>([]);
  const [emptying, setEmptying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [moving, setMoving] = useState(false);
  const rows = list.data?.rows ?? NO_ROWS;
  // columns that are empty for every row on show are left out (most containers keep no code, no bag count)
  const shown = useMemo(
    () => ({
      kod: rows.some((a) => a.kod),
      bags: rows.some((a) => a.kind === "bags" || a.bags != null || a.line_bags != null),
      pgm: rows.some((a) => a.pgm || a.gh),
      updated: rows.some((a) => a.updated),
    }),
    [rows],
  );
  const picks = usePicks({
    picks: picked,
    keyOf: accessionId,
    onPicks: setPicked,
    label: (a: Accession) => t("genebank.selectItem", { position: `${a.kis} ${a.slot ?? ""}` }),
    enabled: canEdit,
  });
  const own = useAccessionColumns(canEdit, shown);
  const columns = useMemo(() => [...picks.columns, ...own], [picks.columns, own]);
  // the server's order (by slot or by name) is the page's own; at most ACCESSION_LIMIT rows (``truncated``)
  const table = useDataTable("genebank.accessions", columns, list.data?.rows, {
    partial: !!list.data?.truncated,
    versions: picks.versions,
  });
  // what is picked and on show (a filter may hide some), in the order ticked: withdrawn and moved in that order
  const chosen = picks.chosen(table.rows);
  return (
    <div className="page fill">
      <PageHeader
        title={t("genebank.title")}
        subtitle={t("genebank.subtitle")}
        actions={
          canEdit && (
            <div className="hstack" style={{ gap: 8 }}>
              <Button size="sm" onClick={() => set({ panel: "intake" })}>
                {t("genebank.storeFromTrial")}
              </Button>
              <ActionMenu actions={[{ label: t("genebank.deleteZeros"), icon: "trash", danger: true, onClick: () => setEmptying(true) }]} />
            </div>
          )
        }
      />
      <div className="chips">
        <button
          type="button"
          className={`chip${search.container ? "" : " on"}`}
          aria-pressed={!search.container}
          onClick={() => set({ container: undefined })}
        >
          <b>{t("genebank.allStores")}</b> <span className="mu">{formatNumber(containers.reduce((n, c) => n + c.accessions, 0))}</span>
        </button>
        {containers.map((c) => (
          <button
            type="button"
            key={c.name}
            className={`chip${c.name === search.container ? " on" : ""}`}
            aria-pressed={c.name === search.container}
            title={t(`genebank.storeKind.${c.kind}`)}
            onClick={() => set({ container: c.name === search.container ? undefined : c.name })}
          >
            <b>{c.name}</b> <span className="mu">{formatNumber(c.accessions)}</span>
          </button>
        ))}
      </div>
      <div className="fill-row">
        <section className="card stack grow" style={{ gap: 0, minWidth: 0, position: "relative" }}>
          <div className="hstack panel-head" style={{ gap: 8, flexWrap: "wrap" }}>
            <SearchBox value={q ?? ""} onChange={setQuery} />
            <Filters search={search} set={set} />
            <UnknownChips search={search} set={set} />
            {search.lines_ev != null && search.lines_of?.length ? (
              <RemovableChip
                tone="g"
                removeLabel={t("genebank.clearTrialLines")}
                onRemove={() => set({ lines_ev: undefined, lines_of: undefined })}
              >
                {t("genebank.trialLinesChip", { ev: search.lines_ev, kis: search.lines_of.join(", ") })}
              </RemovableChip>
            ) : null}
            <span className="grow" />
            <SegmentedControl
              size="xs"
              aria-label={t("genebank.sort")}
              value={order}
              onChange={(v) => set({ order: v as Order })}
              data={(["slot", "name"] as const).map((value) => ({ value, label: t(`genebank.sortBy.${value}`) }))}
            />
            <span className="sm mu">
              {t("genebank.itemCount", { n: formatNumber(rows.length) })}
              {list.data?.truncated && ` · ${t("genebank.truncated")}`}
            </span>
            <Button component="a" size="compact-sm" variant="default" href={getGenebankExportUrl(params)}>
              {t("genebank.excel")}
            </Button>
          </div>
          <QueryFeedback busy={list.isFetching} error={list.error} retry={list.refetch} label={t("async.refreshing")} />
          <DataTable table={table} rowKey={(a) => a.id} className="grow" rowProps={(a) => picks.rowProps(a)} />
          {list.isFetching && !list.data && <Loader size="sm" m="md" />}
          {canEdit && (
            <SelectionBar
              count={chosen.length}
              label={t("genebank.selectedCount", { n: chosen.length })}
              stats={t("genebank.selectedKg", {
                kg: formatNumber(
                  chosen.reduce((n, a) => n + (a.kg ?? 0), 0),
                  3,
                ),
              })}
              actions={[
                { label: t("genebank.withdrawButton"), primary: true, onClick: () => set({ panel: "withdraw" }) },
                { label: t("genebank.moveButton"), onClick: () => setMoving(true) },
                { label: t("genebank.deleteButton"), danger: true, onClick: () => setDeleting(true) },
              ]}
              onClear={() => setPicked([])}
            />
          )}
        </section>
        {canEdit && season && panel && <Splitter cssVar="--gb-panel-w" side="end" min={300} max={720} />}
        {canEdit && season && panel === "intake" && (
          <IntakePanel season={season} containers={containers} container={search.container} onClose={() => set({ panel: undefined })} />
        )}
        {canEdit && season && panel === "withdraw" && (
          <WithdrawPanel
            season={season}
            target={to_kis ? { ev: to_ev ?? season + 1, kis: to_kis } : undefined}
            picked={chosen}
            onClose={() => set({ panel: undefined })}
            onDone={() => setPicked([])}
          />
        )}
      </div>
      {emptying && <EmptyModal container={search.container} onClose={() => setEmptying(false)} />}
      {moving && (
        <MoveModal
          picked={chosen}
          containers={containers}
          onClose={() => setMoving(false)}
          onDone={() => {
            setPicked([]);
            setMoving(false);
          }}
        />
      )}
      {deleting && (
        <DeleteModal
          picked={chosen}
          onClose={() => setDeleting(false)}
          onDone={() => {
            setPicked([]);
            setDeleting(false);
          }}
        />
      )}
    </div>
  );
}

const NO_ROWS: Accession[] = [];
const accessionId = (a: Accession) => a.id;
type Shown = { kod: boolean; bags: boolean; pgm: boolean; updated: boolean };

/** Where the seed came from: site, trial, plot/subplot (the year is a column of its own, sorted as a number). */
const sourceText = (a: Accession) => {
  const plot = a.source_parc != null && a.source_alp ? `${a.source_parc}/${a.source_alp}` : a.source_parc;
  return [a.source_th, a.source_kis, plot].filter((x) => x != null && x !== "").join(" ");
};
const lineOf = (a: { paz: number | null; sid: number | null }) => (a.paz != null && a.sid != null ? { paz: a.paz, sid: a.sid } : null);

/** The accession list's columns that some row on show fills (the tick boxes are ``usePicks``); the amounts are
 *  typed in place with the right to edit. */
function useAccessionColumns(canEdit: boolean, shown: Shown) {
  const { t } = useTranslation();
  return useMemo<Column<Accession>[]>(() => {
    const columns: (Column<Accession> | false)[] = [
      {
        id: "location",
        header: t("genebank.location"),
        value: (a) => `${a.kis} ${a.slot ?? ""}`,
        className: "mono",
      },
      {
        id: "genotype",
        header: t("genebank.genotype"),
        value: (a) => (lineOf(a) ? a.name : null),
        cell: (a) => {
          const key = lineOf(a);
          return key ? <LineLink line={{ key, name: a.name ?? null }} /> : "—";
        },
      },
      {
        id: "line",
        header: "PAz / SID",
        value: (a) => {
          const key = lineOf(a);
          return key ? keyText(key) : null;
        },
        className: "mono",
      },
      kindColumn("integer", (a: Accession) => a.source_ev, {
        id: "source_year",
        header: t("genebank.sourceYear"),
        title: t("genebank.sourceYearHelp"),
        className: "mono sm",
      }),
      { id: "source", header: t("genebank.source"), value: sourceText, className: "mono sm" },
      shown.kod && { id: "kod", header: t("genebank.code"), value: (a) => a.kod, className: "mono sm" },
      shown.pgm && { id: "pgm", header: t("genebank.pgm"), value: (a) => a.pgm, className: "mono sm" },
      shown.pgm && { id: "gh", header: t("genebank.gh"), value: (a) => a.gh, className: "mono sm" },
      { id: "ngbab", header: "NGBAB", value: (a) => a.ngbab, className: "mono sm" },
      shown.bags && {
        id: "bags",
        header: t("genebank.bags"),
        value: (a) => a.bags,
        text: (a) => cellText(a.bags),
        cell: canEdit ? (a) => <AmountCell accession={a} amount="bags" /> : undefined,
        numeric: true,
      },
      shown.bags && {
        id: "line_bags",
        header: t("genebank.total"),
        title: t("genebank.totalHelp"),
        value: (a) => a.line_bags,
        text: (a) => cellText(a.line_bags),
        numeric: true,
        className: "mu",
      },
      { ...kgColumn((a: Accession) => a.kg), cell: canEdit ? (a: Accession) => <AmountCell accession={a} amount="kg" /> : undefined },
      shown.updated && {
        id: "updated",
        header: t("genebank.updated"),
        value: (a) => a.updated,
        text: (a) => fullTime(a.updated),
        className: "sm mu",
      },
    ];
    return columns.filter((c): c is Column<Accession> => !!c);
  }, [t, canEdit, shown]);
}

/** The legacy filters: set in a popover of labelled fields, the active ones shown as chips beside it. */
function Filters({ search, set }: { search: Search; set: (p: Partial<Search>) => void }) {
  const { t } = useTranslation();
  const keys = Object.keys(FILTERS) as FilterKey[];
  const active = keys.filter((f) => search[f] != null);
  return (
    <>
      <Popover position="bottom-start" width={260} shadow="md" withArrow>
        <Popover.Target>
          <Button size="compact-sm" variant="default" leftSection={<Icon name="filter" size={14} />}>
            {t("genebank.filters")}
            {active.length > 0 && (
              <span className="chip g" style={{ marginLeft: 6, height: 18 }}>
                {active.length}
              </span>
            )}
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <div className="stack" style={{ gap: 10 }}>
            {keys
              .filter((f) => search[f] == null)
              .map((f) => (
                <FilterInput key={f} name={f} onSet={(v) => set({ [f]: v })} />
              ))}
            {active.length === keys.length && <span className="sm mu">{t("genebank.allFiltersSet")}</span>}
          </div>
        </Popover.Dropdown>
      </Popover>
      {active.map((f) => (
        <RemovableChip key={f} tone="b" removeLabel={t("genebank.clearFilter")} onRemove={() => set({ [f]: undefined })}>
          {t(`genebank.filter.${f}`, { v: search[f] })}
        </RemovableChip>
      ))}
    </>
  );
}

/** The items whose amount is not known: never counted (bag storage) or never weighed — chips that switch the filter
 *  on and off, always on show (the legacy codes 888 / 999 were found by searching for them). */
function UnknownChips({ search, set }: { search: Search; set: (p: Partial<Search>) => void }) {
  const { t } = useTranslation();
  return (
    <>
      {(["uncounted", "unweighed"] as const).map((f) => (
        <button
          type="button"
          key={f}
          className={`chip${search[f] ? " on" : ""}`}
          aria-pressed={!!search[f]}
          onClick={() => set({ [f]: search[f] ? undefined : true })}
        >
          {t(`genebank.unknown.${f}`)}
        </button>
      ))}
    </>
  );
}

function FilterInput({ name, onSet }: { name: FilterKey; onSet: (v: string | number) => void }) {
  const { t } = useTranslation();
  const [v, setV] = useState("");
  const spec = FILTERS[name];
  const commit = () => {
    const text = v.trim();
    const parsed = parseNumberText(text);
    const n = typeof parsed === "number" ? parsed : Number.NaN;
    if (!text) return;
    if (spec.kind === "text") onSet(text.toUpperCase());
    else if (Number.isFinite(n) && n >= spec.min) onSet(spec.kind === "kg" ? n : Math.trunc(n));
    setV("");
  };
  return (
    <TextInput
      size="xs"
      label={t(`genebank.filterLabel.${name}`)}
      description={t(`genebank.filterHelp.${name}`)}
      value={v}
      onChange={(e) => setV(e.currentTarget.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && commit()}
    />
  );
}

/** A write's run: a message, the gene bank read again, then ``onDone``. */
function useRun(message: (n: number) => string, onDone: () => void) {
  const changed = useGenebankChanged();
  return {
    mutation: {
      onSuccess: (out: { written: number }) => {
        notifications.show({ color: "green", message: message(out.written) });
        changed();
        onDone();
      },
    },
  };
}

function SidePanel({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <aside className="card pad stack gb-panel">
      <div className="hstack" style={{ gap: 8 }}>
        <h2 className="h2 grow">{title}</h2>
        <button type="button" className="chip" onClick={onClose} aria-label={t("genebank.close")}>
          ✕
        </button>
      </div>
      {children}
    </aside>
  );
}

type IntakeForm = Omit<IntakeIn, "ev">;
// the legacy refresh conditions, one at a time (a keypress in one field cleared the others)
const CONDITIONS = ["none", "older_than", "below_kg", "bags_below"] as const;
type Condition = (typeof CONDITIONS)[number];

type IntakeProps = { season: number; containers: Container[]; container: string | undefined; onClose: () => void };
type IntakeSource = IntakeDraft["source"];

/** The intake panel with the user's last intake (its source, container and rules; ``intake_draft``). */
function IntakePanel(props: IntakeProps) {
  const settings = useSettings();
  if (!settings) return <Loader size="sm" m="md" />;
  return <IntakeEditor {...props} stored={settings.intake_draft ?? null} />;
}

const INTAKE_DEFAULTS: IntakeForm = {
  th: "MV",
  trials: [],
  reps: [1, 2, 3],
  container: "HUTO",
  mode: "new",
  amount: "left",
  one_per_line: true,
  unweighed: false,
  debit_ledger: true,
  sisters_from_season: false,
};

function IntakeEditor(props: IntakeProps & { stored: IntakeDraft | null }) {
  const { t } = useTranslation();
  const { trials } = useTrial(props.season, undefined, undefined);
  const [init] = useState(() => props.stored);
  const [form, setForm] = useState<IntakeForm>(() => {
    // a stored intake's rules; the container on show wins, hand-picked plots are not kept (they were of that source),
    // nor the trials of another season (they would narrow a query to codes this season may not have)
    const spec = { ...INTAKE_DEFAULTS, ...((init?.spec ?? {}) as Partial<IntakeForm>), picks: null, query: null };
    if (init?.ev !== props.season) spec.trials = [];
    return props.container ? { ...spec, container: props.container } : spec;
  });
  const [source, setSource] = useState<IntakeSource>(init?.source ?? "trials");
  const [query, setQuery] = useState<number | null>(init?.query ?? null);
  const draft = useMemo<IntakeDraft>(
    () => ({ source, query, ev: props.season, spec: { ...form, picks: null, query: null } as never }),
    [source, query, props.season, form],
  );
  useAutosave("intake_draft", draft, init);
  const saved = useSavedQuery(query, source === "query");
  const chosenQuery = saved.query;
  const upd = (p: Partial<IntakeForm>) => setForm((f) => ({ ...f, ...p }));
  const [pickPlots, setPickPlots] = useState(false);
  const [withAlp, setWithAlp] = useState(false);
  const [condition, setCondition] = useState<Condition>("none");
  const [limit, setLimit] = useState<number | null>(null);
  // the API takes below_kg > 0 and bags_below >= 1: a value it would refuse is not sent (no condition yet)
  const valid = limit != null && (condition === "older_than" ? limit >= 0 : condition === "below_kg" ? limit > 0 : limit >= 1);
  const refreshIf = form.mode === "refresh" && condition !== "none" && valid ? { [condition]: limit } : {};
  // a debit per bag needs a bag count: without one (not counted yet) nothing is debited, the kg per bag is not sent
  const perBag = form.fixed ? form.kg_per_bag : null;
  const chosenTrials = form.trials ?? [];
  const base = {
    ...form,
    kg_per_bag: perBag,
    older_than: null,
    below_kg: null,
    bags_below: null,
    ...refreshIf,
    ev: props.season,
    query: chosenQuery?.id ?? null,
  };
  const hasSource = source === "query" ? chosenQuery != null : chosenTrials.length > 0;
  const candidateBody = hasSource && form.container.trim() ? { ...base, picks: null } : null;
  // saving the query in the builder previews again (its updated_at is in the key)
  const keyed = (key: string) => `${key}:${saved.stamp}`;
  const candidates = usePreview(keyed("genebank-intake-candidates"), candidateBody, (b, signal) =>
    genebankIntakePreview(b, { signal }),
  ).data;
  const body: IntakeIn | null =
    candidateBody && (!pickPlots || form.picks?.length) ? { ...base, picks: pickPlots ? form.picks : null } : null;
  const preview = usePreview(keyed("genebank-intake"), body, (b, signal) => genebankIntakePreview(b, { signal }));
  const plan = preview.data;
  const run = useGenebankIntakeRun(useRun((n) => t("genebank.stored", { n: n }), props.onClose));
  const kind = form.kind ?? plan?.kind ?? props.containers.find((c) => c.name === form.container.trim().toUpperCase())?.kind;
  const bags = kind === "bags";
  const debit = form.debit_ledger !== false;
  const seasonSisters = !!form.sisters_from_season && !pickPlots; // the server keeps one plot per line then
  const sites = [...new Set(trials.filter((o) => source === "query" || chosenTrials.includes(o.kis)).flatMap((o) => o.sites))];
  const taken = (plan?.rows ?? []).filter((r) => r.action !== "skip");
  const unknown = taken.filter((r) => (bags ? r.bags == null : r.kg == null)).length;
  const skipped: Record<string, number> = {};
  for (const r of plan?.rows ?? []) if (r.skip) skipped[r.skip] = (skipped[r.skip] ?? 0) + 1;
  const slots = taken.filter((r) => r.action === "new").map((r) => r.slot);
  const groups = useMemo(
    () =>
      candidateGroups(
        (candidates?.rows ?? []).map((r) => r.plot),
        candidates?.names ?? {},
      ),
    [candidates],
  );
  const picks = form.picks ?? [];
  return (
    <SidePanel title={t("genebank.storeTitle", { ev: props.season, container: form.container })} onClose={props.onClose}>
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      {kind !== "boxes" && (
        <SegmentedControl
          size="xs"
          value={form.mode ?? "new"}
          onChange={(v) => upd({ mode: v as IntakeIn["mode"] })}
          data={[
            { value: "new", label: t("genebank.newItems") },
            { value: "refresh", label: t("genebank.refresh") },
          ]}
        />
      )}
      <Autocomplete
        size="xs"
        label={t("genebank.targetStore")}
        value={form.container}
        onChange={(v) => upd({ container: v })}
        data={props.containers.map((c) => c.name)}
      />
      <SegmentedControl
        size="xs"
        aria-label={t("genebank.sourceChoice")}
        value={source}
        onChange={(v) => {
          setSource(v as IntakeSource);
          upd({ picks: [] });
        }}
        data={(["trials", "query"] as const).map((value) => ({ value, label: t(`genebank.sourceMode.${value}`) }))}
      />
      {source === "query" && (
        <SavedQuerySelect
          value={query}
          onChange={(id) => {
            setQuery(id);
            upd({ picks: [] });
          }}
          required
          description={t("genebank.queryHelp", { ev: props.season })}
        />
      )}
      <MultiSelect
        placeholder={hint("searchSelect")}
        size="xs"
        searchable
        clearable={source === "query"}
        label={source === "query" ? t("genebank.queryTrials") : t("genebank.sourceTrials", { ev: props.season })}
        value={chosenTrials}
        onChange={(v) => upd({ trials: v, picks: [] })}
        data={trials.map((o) => o.kis)}
      />
      {source === "query" && plan?.query && (
        <span className="sm mu">
          {t("genebank.queryResult", { n: formatNumber(plan.query.plots), rows: formatNumber(plan.query.rows) })}
        </span>
      )}
      <Checkbox
        size="xs"
        label={t("genebank.seasonSisters")}
        description={t("genebank.seasonSistersHelp")}
        checked={!!form.sisters_from_season}
        onChange={(e) => upd({ sisters_from_season: e.currentTarget.checked, picks: [] })}
      />
      {form.mode === "refresh" && kind !== "boxes" && (
        <div className="hstack" style={{ gap: 8, alignItems: "flex-end" }}>
          <Select
            size="xs"
            className="grow"
            label={t("genebank.refreshCondition")}
            value={condition}
            onChange={(v) => {
              setCondition((v as Condition | null) ?? "none");
              setLimit(null);
            }}
            data={CONDITIONS.map((value) => ({ value, label: t(`genebank.condition.${value}`) }))}
          />
          {condition !== "none" && (
            <NumberInput
              size="xs"
              w={100}
              label={t("genebank.conditionValue")}
              allowDecimal={condition === "below_kg"}
              min={condition === "bags_below" ? 1 : 0}
              value={limit ?? ""}
              onChange={(v) => setLimit(num(v))}
            />
          )}
        </div>
      )}
      <Select
        size="xs"
        label={t("parity.storage")}
        value={form.kind ?? "auto"}
        onChange={(v) => upd({ kind: v === "auto" ? null : (v as IntakeIn["kind"]) })}
        data={[
          { value: "auto", label: t("parity.automatic") },
          ...(["single", "bags", "boxes"] as const).map((value) => ({ value, label: t(`parity.${value}`) })),
        ]}
      />
      <Checkbox
        label={t("parity.allPlots")}
        checked={pickPlots}
        onChange={(e) => {
          setPickPlots(e.currentTarget.checked);
          upd({ picks: [], one_per_line: !e.currentTarget.checked });
        }}
      />
      {pickPlots && (
        <>
          <MultiSelect
            placeholder={hint("searchSelect")}
            searchable
            label={t("parity.sourcePlots")}
            data={groups}
            value={picks.map(pickKey)}
            onChange={(values) => upd({ picks: picksFor(values, picks) })}
          />
          <Checkbox size="xs" label={t("genebank.sourceSubplot")} checked={withAlp} onChange={(e) => setWithAlp(e.currentTarget.checked)} />
          {withAlp &&
            picks.map((p) => (
              <NumberInput
                key={pickKey(p)}
                size="xs"
                label={t("genebank.sourceSubplotLabel", { plot: `${p.kis} ${p.parc}` })}
                min={0}
                max={999}
                allowDecimal={false}
                value={p.e_alp ?? ""}
                onChange={(v) => upd({ picks: picks.map((x) => (x === p ? { ...x, e_alp: num(v) } : x)) })}
              />
            ))}
        </>
      )}
      <div className="hstack" style={{ gap: 8 }}>
        <Select
          size="xs"
          w={80}
          label={t("genebank.site")}
          value={form.th}
          onChange={(v) => v && upd({ th: v })}
          data={sites.length ? sites : [form.th ?? "MV"]}
        />
        {!bags && debit && (
          <SegmentedControl
            size="xs"
            value={form.amount ?? "left"}
            onChange={(v) => upd({ amount: v as IntakeIn["amount"] })}
            data={[
              { value: "left", label: t("genebank.remainder") },
              { value: "fixed", label: t("genebank.fixed") },
            ]}
          />
        )}
      </div>
      <Checkbox
        size="xs"
        label={t("genebank.deductFromSeedLog")}
        description={t("genebank.deductFromSeedLogHelp")}
        checked={debit}
        onChange={(e) => upd({ debit_ledger: e.currentTarget.checked, unweighed: false })}
      />
      {!bags && debit && form.amount === "left" && (
        <div className="hstack" style={{ gap: 10 }}>
          <span className="sm mu">{t("genebank.weightSource")}</span>
          {[1, 2, 3].map((r) => (
            <Checkbox
              key={r}
              size="xs"
              label={`${r}.`}
              checked={form.reps?.includes(r)}
              onChange={(e) =>
                upd({ reps: e.currentTarget.checked ? [...(form.reps ?? []), r].sort() : (form.reps ?? []).filter((x) => x !== r) })
              }
            />
          ))}
        </div>
      )}
      {(bags || form.amount === "fixed" || !debit) && (
        <NumberInput
          size="xs"
          label={bags ? t("genebank.bagsPerItem") : t("genebank.kgPerItem")}
          description={bags ? t("genebank.bagsNotCounted") : debit ? undefined : t("genebank.kgNotMeasured")}
          allowDecimal={!bags}
          min={0}
          value={form.fixed ?? ""}
          onChange={(v) => upd({ fixed: num(v) })}
        />
      )}
      {bags && debit && !!form.fixed && (
        <NumberInput
          size="xs"
          label={t("genebank.kgPerBag")}
          description={t("genebank.kgPerBagHelp")}
          min={0}
          value={form.kg_per_bag ?? ""}
          onChange={(v) => upd({ kg_per_bag: num(v) })}
        />
      )}
      <Checkbox
        size="xs"
        label={t("genebank.onePerLine")}
        checked={!!form.one_per_line || seasonSisters}
        disabled={seasonSisters}
        onChange={(e) => upd({ one_per_line: e.currentTarget.checked })}
      />
      {!bags && debit && (
        <Checkbox
          size="xs"
          label={t("genebank.seedLogOnly")}
          checked={!!form.unweighed}
          onChange={(e) => upd({ unweighed: e.currentTarget.checked })}
        />
      )}
      {plan && (
        <div className="note sm stack" style={{ gap: 4 }}>
          <b>
            {bags
              ? t("genebank.preview", { n: taken.length, bags: formatNumber(taken.reduce((n, r) => n + (r.bags ?? 0), 0)) })
              : t("genebank.previewKg", {
                  n: taken.length,
                  kg: formatNumber(
                    taken.reduce((n, r) => n + (r.debit ?? 0), 0),
                    2,
                  ),
                })}
          </b>
          {unknown > 0 && <span>{t("genebank.unknownQuantity", { n: unknown })}</span>}
          {slots.length > 0 && (
            <span>
              {t("genebank.slots", {
                container: form.container,
                positions: `${slots.slice(0, 6).join(", ")}${slots.length > 6 ? "…" : ""}`,
              })}
            </span>
          )}
          {Object.entries(skipped).map(([k, n]) => (
            <span key={k} className="mu">
              {t(`genebank.skip.${k}`, { n: n })}
            </span>
          ))}
        </div>
      )}
      <Button
        disabled={!taken.length || !body}
        loading={run.isPending}
        onClick={() => body && plan && run.mutate({ data: { ...body, token: plan.token } })}
      >
        {t("genebank.storeRun", { n: taken.length })}
      </Button>
    </SidePanel>
  );
}

function WithdrawPanel(props: {
  season: number;
  target: { ev: number; kis: string } | undefined;
  picked: Accession[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [ev, setEv] = useState(props.target?.ev ?? props.season + 1);
  const { years } = useSeason();
  const season = useCreateSeason();
  const open = years.includes(ev);
  const { trials } = useTrial(open ? ev : undefined, undefined, undefined);
  const [kis, setKis] = useState(props.target?.kis ?? "");
  const [start, setStart] = useState<number | null>(null);
  const [sor, setSor] = useState<number | null>(null);
  const [suffix, setSuffix] = useState("");
  const [take, setTake] = useState<number | null>(null); // from each accession; empty: all it holds
  const [skipAlready, setSkipAlready] = useState(false); // the legacy "Item already selected": No
  const body: WithdrawIn | null =
    props.picked.length && kis.trim() && open
      ? {
          ids: props.picked.map((a) => a.id),
          take,
          ev,
          kis: kis.trim(),
          start,
          sor,
          code_suffix: suffix || null,
          skip_already: skipAlready,
        }
      : null;
  const preview = usePreview("genebank-withdraw", body, (b, signal) => genebankWithdrawPreview(b, { signal }));
  const plan = preview.data;
  const done = () => {
    props.onDone();
    props.onClose();
  };
  const run = useGenebankWithdrawRun(useRun((n) => t("genebank.withdrawn", { n: n }), done));
  const problems = (plan?.rows ?? []).filter((r) => r.problem).length;
  const already = (plan?.rows ?? []).filter((r) => r.already?.length).length;
  const lineBags = useMemo(() => new Map(props.picked.map((a) => [a.id, a.line_bags])), [props.picked]);
  const anyBags = props.picked.some((a) => a.line_bags != null);
  const names = plan?.names;
  const columns = useMemo<Column<WithdrawRow>[]>(() => {
    const nameOf = (r: WithdrawRow) => names?.[`${r.stock.paz}/${r.stock.sid}`] ?? null;
    const remains = (r: WithdrawRow) => (r.problem ? t(`genebank.problem.${r.problem}`) : formatNumber(r.left));
    const columns: (Column<WithdrawRow> | false)[] = [
      {
        id: "location",
        header: t("genebank.location"),
        value: (r) => `${r.stock.kis} ${r.stock.slot}`,
        className: "mono",
      },
      {
        id: "genotype",
        header: t("genebank.genotype"),
        value: nameOf,
        cell: (r) => (
          <>
            <LineLink line={{ key: { paz: r.stock.paz, sid: r.stock.sid }, name: nameOf(r) }} />
            {r.already?.length ? (
              <span className="chip y" style={{ marginLeft: 6 }}>
                {t("genebank.alreadySownRow", { parc: r.already.join(", ") })}
              </span>
            ) : null}
          </>
        ),
      },
      anyBags && {
        id: "line_bags",
        header: t("genebank.total"),
        title: t("genebank.totalHelp"),
        value: (r) => lineBags.get(r.stock.id),
        text: (r) => cellText(lineBags.get(r.stock.id)),
        numeric: true,
        className: "mu",
      },
      { id: "plot", header: t("genebank.plot"), value: (r) => r.parc, text: (r) => String(r.parc), numeric: true, className: "mono" },
      { id: "take", header: t("genebank.take"), value: (r) => r.take, numeric: true },
      {
        id: "left",
        header: t("genebank.remains"),
        value: (r) => (r.problem ? null : r.left),
        text: remains,
        numeric: true,
      },
    ];
    return columns.filter((c): c is Column<WithdrawRow> => !!c);
  }, [t, names, anyBags, lineBags]);
  const table = useDataTable("genebank.withdrawal", columns, plan?.rows);
  return (
    <SidePanel title={t("genebank.withdrawTitle", { n: props.picked.length })} onClose={props.onClose}>
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <div className="hstack" style={{ gap: 8 }}>
        <YearInput size="xs" label={t("genebank.year")} value={ev} onChange={(v) => setEv(num(v) ?? props.season + 1)} />
        <Autocomplete
          size="xs"
          className="grow"
          label={t("genebank.targetTrial")}
          value={kis}
          onChange={setKis}
          data={trials.map((o) => o.kis)}
        />
      </div>
      {!open && <SeasonMissing ev={ev} creating={season.pending} onCreate={() => season.create(ev)} />}
      <div className="hstack" style={{ gap: 8 }}>
        <NumberInput
          size="xs"
          label={t("genebank.firstPlot")}
          placeholder={t("genebank.firstPlotHelp")}
          min={1}
          allowDecimal={false}
          value={start ?? ""}
          onChange={(v) => setStart(num(v))}
        />
        <NumberInput
          size="xs"
          w={80}
          label={t("genebank.row")}
          min={1}
          allowDecimal={false}
          value={sor ?? ""}
          onChange={(v) => setSor(num(v))}
        />
        <TextInput size="xs" w={80} label={t("genebank.codeSuffix")} value={suffix} onChange={(e) => setSuffix(e.currentTarget.value)} />
      </div>
      <NumberInput
        size="xs"
        label={t("genebank.quantity")}
        description={t("genebank.quantityHelp")}
        min={0}
        value={take ?? ""}
        onChange={(v) => setTake(num(v))}
      />
      {preview.isError && <p className="note warn sm">{t("genebank.withdrawError")}</p>}
      {(already > 0 || skipAlready) && (
        <div className="note warn sm stack" style={{ gap: 4 }}>
          {already > 0 && <span>{t("genebank.alreadySown", { n: already })}</span>}
          {plan && plan.skipped.length > 0 && <span>{t("genebank.skippedSown", { n: plan.skipped.length })}</span>}
          <Checkbox
            size="xs"
            label={t("genebank.skipAlreadySown")}
            checked={skipAlready}
            onChange={(e) => setSkipAlready(e.currentTarget.checked)}
          />
        </div>
      )}
      {plan && (
        <div className="stack" style={{ maxHeight: 280, minHeight: 0 }}>
          <DataTable table={table} rowKey={(r) => r.stock.id} rowProps={(r) => ({ className: r.problem ? "gb-problem" : undefined })} />
        </div>
      )}
      <Button
        disabled={!plan || !body || problems > 0 || !plan.rows.length}
        loading={run.isPending}
        onClick={() => body && plan && run.mutate({ data: { ...body, token: plan.token } })}
      >
        {t("genebank.withdrawRun", { n: plan?.rows.length ?? props.picked.length })}
      </Button>
    </SidePanel>
  );
}

/** Accessions deleted (the legacy Del key in the container grid), and, when asked for, each one's debit given back to
 *  its source plot's ledger. Not by default: an old vintage discarded must not put its seed back on an old plot. */
function DeleteModal(props: { picked: Accession[]; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [refund, setRefund] = useState(false);
  const body = { ids: props.picked.map((a) => a.id), refund };
  const preview = usePreview("genebank-delete", body, (b, signal) => genebankDeletePreview(b, { signal }), 0);
  const plan = preview.data;
  const run = useGenebankDeleteRun(useRun((n) => t("genebank.deletedItems", { n: n }), props.onDone));
  const rows = plan?.rows ?? [];
  const refunded = rows.filter((r) => r.refund != null);
  const ambiguous = rows.filter((r) => r.problem === "ambiguous_refund").length;
  const drawn = rows.filter((r) => r.problem === "partial_refund").length; // bags taken from it: the part left is not known
  const partial = refunded.filter((r) => r.debit != null && r.refund != null && r.refund < r.debit - 1e-9).length;
  const shipped = rows.filter((r) => r.shipped).length;
  const names = plan?.names;
  const columns = useMemo<Column<Removal>[]>(() => {
    const nameOf = (r: Removal) => names?.[`${r.paz}/${r.sid}`] ?? (r.paz != null ? `${r.paz}/${r.sid}` : null);
    const amount = (r: Removal) => (r.kg != null ? formatNumber(r.kg, 3) : r.bags != null ? `${r.bags} ${t("genebank.bags")}` : "—");
    const restored = (r: Removal) =>
      r.problem === "ambiguous_refund"
        ? t("genebank.ambiguous")
        : r.problem === "partial_refund"
          ? t("genebank.partial")
          : r.refund != null
            ? formatNumber(r.refund, 3)
            : "—";
    const columns: (Column<Removal> | false)[] = [
      { id: "location", header: t("genebank.location"), value: (r) => `${r.kis} ${r.slot ?? ""}`, className: "mono" },
      { id: "genotype", header: t("genebank.genotype"), value: nameOf },
      { id: "kg", header: "kg", value: (r) => r.kg ?? r.bags, text: amount, numeric: true },
      refund && {
        id: "refund",
        header: t("genebank.restoreColumn"),
        value: (r) => (r.problem ? null : r.refund),
        text: restored,
        numeric: true,
        cellProps: (r) => ({
          title: r.debit != null ? t("genebank.deductionOriginal", { kg: formatNumber(r.debit, 3) }) : undefined,
        }),
      },
    ];
    return columns.filter((c): c is Column<Removal> => !!c);
  }, [t, names, refund]);
  const table = useDataTable("genebank.deletion", columns, plan?.rows);
  return (
    <ConfirmModal
      title={t("genebank.deleteTitle", { n: props.picked.length })}
      confirm={t("genebank.deleteRun", { n: rows.length })}
      danger
      disabled={!plan || ambiguous + drawn > 0}
      loading={run.isPending}
      onConfirm={() => plan && run.mutate({ data: { ...body, token: plan.token } })}
      onClose={props.onClose}
    >
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <Radio.Group value={refund ? "refund" : "only"} onChange={(v) => setRefund(v === "refund")} label={t("genebank.deleteHow")}>
        <div className="stack" style={{ gap: 8, marginTop: 6 }}>
          <Radio value="only" label={t("genebank.deleteOnly")} description={t("genebank.deleteOnlyHelp")} />
          <Radio value="refund" label={t("genebank.deleteRestore")} description={t("genebank.deleteRestoreHelp")} />
        </div>
      </Radio.Group>
      {plan && (
        <div className="stack" style={{ maxHeight: 220, minHeight: 0 }}>
          <DataTable table={table} rowKey={(r) => r.id} rowProps={(r) => ({ className: r.problem ? "gb-problem" : undefined })} />
        </div>
      )}
      {plan && refund && (
        <div className="sm stack" style={{ gap: 4 }}>
          {refunded.length > 0 && (
            <span>
              {t("genebank.deleteRestored", {
                n: refunded.length,
                kg: formatNumber(
                  refunded.reduce((n, r) => n + (r.refund ?? 0), 0),
                  3,
                ),
              })}
            </span>
          )}
          {partial > 0 && <span className="mu">{t("genebank.deletePartial", { n: partial })}</span>}
          {rows.length - refunded.length - ambiguous - drawn > 0 && (
            <span className="mu">{t("genebank.deleteNoDeduction", { n: rows.length - refunded.length - ambiguous - drawn })}</span>
          )}
          {ambiguous > 0 && <span className="note warn">{t("genebank.deleteAmbiguous", { n: ambiguous })}</span>}
          {drawn > 0 && <span className="note warn">{t("genebank.deleteBagsTaken", { n: drawn })}</span>}
        </div>
      )}
      {shipped > 0 && <span className="sm mu">{t("genebank.deleteShipped", { n: shipped })}</span>}
    </ConfirmModal>
  );
}

function EmptyModal({ container, onClose }: { container: string | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const [every, setEvery] = useState(false); // with a container chosen, only it unless every container is asked for
  const [unfilled, setUnfilled] = useState(false); // also the items never counted or weighed (the legacy 888 bags)
  const one = container && !every ? container : undefined;
  const body = { containers: one ? [one] : [], unfilled };
  const preview = usePreview("genebank-empty", body, (b, signal) => genebankEmptyPreview(b, { signal }), 0);
  const plan = preview.data;
  const run = useGenebankEmptyRun(useRun((n) => t("genebank.zerosDeleted", { n: n }), onClose));
  return (
    <ConfirmModal
      title={t("genebank.zerosTitle")}
      confirm={t("genebank.zerosDelete", { n: plan?.count ?? 0 })}
      danger
      disabled={!plan?.count}
      loading={run.isPending}
      onConfirm={() => plan && run.mutate({ data: { ...body, token: plan.token } })}
      onClose={onClose}
    >
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      {container ? (
        <Radio.Group value={every ? "all" : "one"} onChange={(v) => setEvery(v === "all")} label={t("genebank.zerosScope")}>
          <div className="stack" style={{ gap: 6, marginTop: 6 }}>
            <Radio value="one" label={t("genebank.zerosOne", { container: container })} />
            <Radio value="all" label={t("genebank.zerosAll")} />
          </div>
        </Radio.Group>
      ) : (
        <span className="sm">{t("genebank.zerosAll")}</span>
      )}
      <Checkbox
        label={t("genebank.zerosUnfilled")}
        description={t("genebank.zerosUnfilledHelp")}
        checked={unfilled}
        onChange={(e) => setUnfilled(e.currentTarget.checked)}
      />
      {plan && <b>{t("genebank.zerosCount", { n: plan.count })}</b>}
      {plan && unfilled && <span className="sm">{t("genebank.zerosUnfilledCount", { n: plan.unfilled ?? 0 })}</span>}
    </ConfirmModal>
  );
}

/** Accessions moved to another container (one that exists or a new name, e.g. a "999" placeholder to renumber later):
 *  each takes a new position — the container's gaps first, or from a first position on — and keeps counting its seed
 *  the way it did (bags or kg). One undoable operation. */
function MoveModal(props: { picked: Accession[]; containers: Container[]; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [container, setContainer] = useState("");
  const [start, setStart] = useState<number | null>(null);
  const target = container.trim();
  const body = target && props.picked.length ? { ids: props.picked.map((a) => a.id), container: target, start } : null;
  const preview = usePreview("genebank-move", body, (b, signal) => genebankMovePreview(b, { signal }));
  const plan = preview.data;
  const run = useGenebankMoveRun(useRun((n) => t("genebank.moved", { n, container: plan?.container ?? target }), props.onDone));
  const drawn = (plan?.rows ?? []).filter((r) => r.drawn).length;
  const held = (plan?.rows ?? []).filter((r) => r.held).length;
  const isNew = !!target && !props.containers.some((c) => c.name === target.toUpperCase());
  const names = plan?.names;
  const into = plan?.container ?? "";
  const columns = useMemo<Column<Move>[]>(() => {
    const nameOf = (r: Move) => names?.[`${r.paz}/${r.sid}`] ?? null;
    return [
      { id: "from", header: t("genebank.moveFrom"), value: (r) => `${r.kis} ${r.slot ?? ""}`, className: "mono" },
      { id: "to", header: t("genebank.moveTo"), value: (r) => r.to, text: (r) => `${into} ${r.to}`, className: "mono" },
      {
        id: "genotype",
        header: t("genebank.genotype"),
        value: nameOf,
        cell: (r) => (
          <>
            {nameOf(r) ?? "—"}
            {r.held && (
              <span className="chip y" style={{ marginLeft: 6 }}>
                {t("genebank.moveHeldRow")}
              </span>
            )}
          </>
        ),
      },
    ];
  }, [t, names, into]);
  const table = useDataTable("genebank.move", columns, plan?.rows);
  return (
    <ConfirmModal
      title={t("genebank.moveTitle", { n: props.picked.length })}
      confirm={t("genebank.moveRun", { n: plan?.rows.length ?? props.picked.length })}
      disabled={!plan || !body}
      loading={run.isPending}
      onConfirm={() => body && plan && run.mutate({ data: { ...body, token: plan.token } })}
      onClose={props.onClose}
    >
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <Autocomplete
        label={t("genebank.moveContainer")}
        description={isNew ? t("genebank.moveNewContainer") : t("genebank.moveContainerHelp")}
        value={container}
        onChange={setContainer}
        data={props.containers.map((c) => c.name)}
      />
      <NumberInput
        label={t("genebank.moveStart")}
        description={t("genebank.moveStartHelp")}
        min={1}
        allowDecimal={false}
        value={start ?? ""}
        onChange={(v) => setStart(num(v))}
      />
      {plan && (
        <div className="stack" style={{ maxHeight: 240, minHeight: 0 }}>
          <DataTable table={table} rowKey={(r) => r.id} />
        </div>
      )}
      {held > 0 && <span className="note warn sm">{t("genebank.moveHeld", { n: held })}</span>}
      {drawn > 0 && <span className="note warn sm">{t("genebank.moveDrawn", { n: drawn })}</span>}
    </ConfirmModal>
  );
}
